import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, OnInit } from '@angular/core';
import { FormControl, ReactiveFormsModule } from "@angular/forms";
import { MatButtonModule } from "@angular/material/button";
import { MatCardModule } from "@angular/material/card";
import { MatOptionModule } from "@angular/material/core";
import { MatDialog, MatDialogModule, MatDialogRef } from "@angular/material/dialog";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatIconModule } from "@angular/material/icon";
import { MatProgressSpinnerModule } from "@angular/material/progress-spinner";
import { MatSelectModule } from "@angular/material/select";
import { MatTooltipModule } from "@angular/material/tooltip";
import { Store } from "@ngxs/store";
import { XMLParser } from "fast-xml-parser";
import { catchError, EMPTY, Observable, timer } from "rxjs";
import { PermissionService } from "../../../services";
import { Int32StringKeyValuePair, Language, Product, TextService, Version } from "../../../services/api";
import { AppState } from "../../../state";
import { ClearImportedTranslation, ImportedTranslation, ProductApiStatusState } from "../../product/state";
import { AlertDialogComponent } from "../alert-dialog/alert-dialog.component";
import { ConfirmDialogComponent } from "../confirm-dialog/confirm-dialog.component";
import { SubscriptionHandlerComponent } from "../subscription-handler/subscription-handler.component";

@Component({
    selector: 'app-import-dialog',
    imports: [CommonModule, MatFormFieldModule, MatOptionModule, MatSelectModule, ReactiveFormsModule, MatButtonModule, MatDialogModule, MatIconModule, MatCardModule, MatProgressSpinnerModule, MatTooltipModule],
    templateUrl: './import-dialog.component.html',
    styleUrl: './import-dialog.component.css',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class ImportDialogComponent extends SubscriptionHandlerComponent implements OnInit
{
    languages$: Observable<Language[]> = this.store.select(AppState.languages);

    importedTranslation$: Observable<boolean | null> = this.store.select(ProductApiStatusState.importedTranslation);

    product: Product | null = null;

    version: Version | null = null;

    // Import button pressed flag
    importPressed: boolean = false;

    file: File;
    fileName: string = "";
    // Not used
    requiredFileType: string;

    selectedLanguage = new FormControl<Language | null>(null);

    constructor(public permissionService: PermissionService,
        private textService: TextService,
        private store: Store,
        private dialog: MatDialog,
        private dialogRef: MatDialogRef<ImportDialogComponent>)
    {
        super();
    }

    ngOnInit()
    {
        this.store.dispatch(new ClearImportedTranslation())

        this.subscribeTo(this.languages$, languages =>
        {
            if (languages)
            {
                // Filter out Developer-Language for import
                let availableLanguages = languages.filter(x => x.id !== 1)
                if (availableLanguages.length > 0)
                    this.selectedLanguage.setValue(availableLanguages[0])
            }
        });
    }

    // Upload a file event
    uploadFile(event: any)
    {
        this.file = event.target.files[0];
        if (this.file)
            this.fileName = this.file.name;

        if (this.file.type !== "text/xml")
        {
            this.fileName = "";
            this.dialog.open(AlertDialogComponent, {
                data: "Not a supported Filetype",
                disableClose: true,
                width: 'auto',
                height: 'auto'
            });
        }
    }

    // Import the Translation file
    importFile()
    {
        if (!this.selectedLanguage.value)
        {
            return;
        }

        const parser = new XMLParser({ ignoreAttributes: false, attributeNamePrefix: "", textNodeName: "value" });

        // Get Language Code from filename
        let lanCode = this.fileName.replace('.xml', '').slice(-5);

        // Open confirm dialog (with additional information if the Language Code of the file is different from the selected Language)
        const dialogRef = this.dialog.open(ConfirmDialogComponent, {
            data: {
                title: "Import Translation File",
                message: lanCode !== this.selectedLanguage.value.languageCode ?
                    "The provided file seems to be for another language than your selected language. Are you sure you want to proceed?" :
                    "Are you sure you want to import this translation file?",
                confirmButtonText: "Import",
                cancelButtonText: "Cancel"
            },
            disableClose: true,
            width: 'auto',
            height: 'auto'
        });

        dialogRef.afterClosed().subscribe(result =>
        {
            // If the user confirmed the import, parse the file and import the translations
            if (result)
            {
                this.importPressed = true;

                this.file.text().then(str =>
                {
                    try
                    {
                        let jObj = parser.parse(str);
                        let pairs: Int32StringKeyValuePair[] = [];
                        // Check if the file includes text elements
                        if (jObj.Root.Text)
                        {
                            // Create a key-value pair for each text element
                            if (jObj.Root.Text.length)
                            {
                                for (let i = 0; i < jObj.Root.Text.length; i++)
                                {
                                    pairs[i] =
                                    {
                                        key: jObj.Root.Text[i].Id,
                                        value: jObj.Root.Text[i].value
                                    }
                                }
                            } else
                            {
                                pairs[0] =
                                {
                                    key: jObj.Root.Text.Id,
                                    value: jObj.Root.Text.value
                                }
                            }
                        } else
                        {
                            // Error if the file doesn't include text elements
                            this.dialog.open(AlertDialogComponent, {
                                data: "Your xml file does not include any text elements.",
                                disableClose: true,
                                width: 'auto',
                                height: 'auto'
                            });
                            this.importPressed = false;
                            return;
                        }

                        // Close the dialog 2 seconds after importing
                        this.subscribeTo(this.importedTranslation$, x =>
                            x && timer(2000).subscribe(() => this.dialogRef.close(true))
                        );

                        if (!this.selectedLanguage.value)
                        {
                            return;
                        }
                        // Use the TextService to import the translations with the key-value pairs and the selected language
                        this.subscribeTo(this.textService.importTranslations(pairs, this.selectedLanguage.value.id, "response").pipe(
                            catchError(() =>
                            {
                                this.store.dispatch(new ImportedTranslation(false));
                                return EMPTY;
                            })), result =>
                            {
                                this.store.dispatch(new ImportedTranslation(result.status === 200));
                            })
                        return;
                    } catch (err)
                    {
                        // Error if the file isn't a valid xml file
                        this.dialog.open(AlertDialogComponent, {
                            data: "Your file isn't a valid xml file.",
                            disableClose: true,
                            width: 'auto',
                            height: 'auto'
                        });
                        this.importPressed = false;
                        return;
                    }
                })
            }
        });

    }
}
